/**
 * The node tree of a space, drawn (§4.2, §4.5).
 *
 * Presentational and complete: it fetches nothing, filters nothing, and decides
 * nothing about access. Whatever rows it is handed, it draws — which is the
 * only honest thing to do with a list that arrived already filtered by
 * `permission_visible_nodes`. `space-tree.tsx` is the component that fetches;
 * this one exists separately so a document page can render the tree it was
 * given without a second request, and so the shape logic has somewhere to be
 * tested from.
 *
 * "The UI needs no collective noun; three icons in one tree is legible" (§4.5).
 * There are no icons in this shell's kit, so the three types are three badge
 * tones and a word — the same ones `routes/spaces/detail.tsx` already uses.
 */

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { LapsedReview, TreeNode } from '../../api/types.ts';
import { EmptyState } from '../ui/feedback.tsx';
import { buildNodeTree, type NodeTreeItem } from './build.ts';
import { documentLinkProps, nodeLinkProps } from './links.ts';
import { ExternalLinkMarker, NodeTypeBadge, ReviewBadge } from './markers.tsx';

/**
 * What a row can say about a document, beyond what a `node` row carries.
 *
 * Keyed by node id and passed in rather than fetched per row, because the only
 * endpoint that answers it in bulk answers it for every space at once — see
 * `space-tree.tsx`. A node absent from the map is a node with *nothing known*
 * about its review date, which is not the same as one known to be current, and
 * the markers are careful not to claim otherwise.
 */
export type ReviewIndex = ReadonlyMap<string, LapsedReview>;

export interface NodeTreeProps {
  nodes: readonly TreeNode[];
  /** The node whose page this is, rendered as text rather than a link to itself. */
  activeNodeId?: string;
  reviews?: ReviewIndex;
  /** Per-row controls — the space page's delete button, and nothing by default. */
  actions?: (node: TreeNode) => ReactNode;
  emptyState?: ReactNode;
}

function NodeRow({
  item,
  activeNodeId,
  reviews,
  actions,
}: {
  item: NodeTreeItem;
  activeNodeId: string | undefined;
  reviews: ReviewIndex | undefined;
  actions: ((node: TreeNode) => ReactNode) | undefined;
}) {
  const { node } = item;
  const review = reviews?.get(node.id);
  const isActive = node.id === activeNodeId;
  const linkProps = node.type === 'document' ? documentLinkProps(node.id) : nodeLinkProps(node.id);

  return (
    <>
      <li>
        <div
          className="flex flex-wrap items-center gap-2 border-b border-secondary px-4 py-2.5 hover:bg-secondary"
          // Indentation by depth, matching the space page. `depth` comes from
          // the rows this person can see, so a node whose parent is invisible
          // to them sits at the top rather than floating at an indent that
          // implies an ancestor they cannot open (§4.6).
          style={{ paddingLeft: `${1 + item.depth * 1.5}rem` }}
        >
          {item.depth > 0 ? (
            <span aria-hidden="true" className="text-quaternary">
              └
            </span>
          ) : null}
          {isActive ? (
            <span aria-current="page" className="font-semibold text-primary">
              {node.title}
            </span>
          ) : (
            <Link {...linkProps} className="font-medium text-primary no-underline hover:underline">
              {node.title}
            </Link>
          )}
          <NodeTypeBadge type={node.type} />
          {node.type === 'link' ? <ExternalLinkMarker /> : null}
          {/*
           * The review marker and no status badge. The only per-document data
           * this tree has is the lapsed list, so a status badge would appear on
           * stale documents and on nothing else — which reads as "these are the
           * ones with a status" rather than "these are the ones to re-read".
           * `ReviewBadge` already distinguishes whether the 07:00 sweep has
           * caught up, which is the only part of the status that differs here.
           */}
          {review ? <ReviewBadge reviewBy={review.reviewBy} status={review.status} /> : null}
          {actions ? (
            <span className="ml-auto flex items-center gap-2">{actions(node)}</span>
          ) : null}
        </div>
      </li>
      {item.children.map((child) => (
        <NodeRow
          key={child.node.id}
          item={child}
          activeNodeId={activeNodeId}
          reviews={reviews}
          actions={actions}
        />
      ))}
    </>
  );
}

export function NodeTree({ nodes, activeNodeId, reviews, actions, emptyState }: NodeTreeProps) {
  const tree = useMemo(() => buildNodeTree(nodes), [nodes]);

  if (tree.length === 0) {
    return (
      emptyState ?? (
        <EmptyState
          title="Nothing you can reach here"
          description="Either this space has no nodes, or no grant reaches you on any of them. Both look the same from here, which is the point — an unreachable node is not refused, it is absent."
        />
      )
    );
  }

  return (
    <ul className="flex flex-col">
      {tree.map((item) => (
        <NodeRow
          key={item.node.id}
          item={item}
          activeNodeId={activeNodeId}
          reviews={reviews}
          actions={actions}
        />
      ))}
    </ul>
  );
}
